<template>
  <UikitForm
    class="osk-product-comments-form"
    @submit-validate-success="submitSuccess"
  >
    <ProductFileAttach
      v-if="isCommentTextAndPhotos || true"
      ref="$fileAttach"
      class="osk-product-comments-form__images"
      @file-attach="handleFileAttach"
    />

    <div class="osk-product-comments-form__wrapper">
      <ProductCommentsAttachedImages
        class="osk-product-comments-form__attached-images"
        :attached-imgs="base64Imgs"
        @remove-img="removeImg"
      />

      <div class="osk-product-comments-form__content">
        <UikitFormInput
          ref="$input"
          v-model="commentText"
          :class="{
            'osk-product-comments-form__input': true,
            'osk-product-comments-form__input_round-disable': !!base64Imgs.length,
          }"
          name="text"
          placeholder="Сообщение..."
          background="grey-4"
          textarea
          :rows="1"
          autoresize
          required
          round
          no-error-message
        />

        <UikitButton
          class="osk-product-comments-form__button"
          size="s"
          icon="ArrowRight"
          round
          :loading="loading"
        />
      </div>
    </div>

    <notifications
      group="productComments"
      position="bottom right"
      :duration="-1"
      width="auto"
    >
      <template #body="{ item, close }">
        <div class="osk-product-comments-form__notification">
          <p class="osk-product-comments-form__notification-title">
            {{ item.title }}
          </p>

          <div
            class="osk-product-comments-form__notification-text"
            v-html="item.text"
          />

          <UikitButton
            full-width
            type="button"
            @click="close"
          >
            Понятно
          </UikitButton>
        </div>
      </template>
    </notifications>
  </UikitForm>
</template>

<script setup lang="ts">
import { useNotification } from '@kyvg/vue3-notification';

import { useProductService } from '~/services/product';
import {
  CommentDTO, ProductCommentBase64ImagesDataDTO, ProductDTO, ProductDTORes,
} from '~/restAPI/data-contracts';
import { IAttachedImg } from '~/types/components/attachedImages';
import { AnalyticsEvents } from '~/types/analytics';
import type { TSubmitSuccess } from '~/types/components/form';
import { img2Base64 } from '~/utils/files';
import { CustomHTTPError } from '~/restAPI/error';

const props = defineProps<{
  productId: number
  commentPostMode?: 'TEXT_AND_PHOTOS' | 'TEXT' | 'NONE'
  replyComment?: CommentDTO
  refresh: (comment?: CommentDTO) => void
  noFocus?: boolean
  product: ProductDTORes | ProductDTO
}>();

const emits = defineEmits<{
  (e: 'update:replyComment', value?: CommentDTO): void,
}>();

const $input = ref();

const { postComment, postCommentNoPhotos } = useProductService();
const { $addEvent } = useNuxtApp();
const { isDesktop } = useUiBreakpoints();

const replyCommentNick = computed(() => {
  const nick = props.replyComment?.publisher?.nickname;

  if (!nick) return '';

  return `@${nick}, `;
});

const commentText = ref('');
const loading = ref(false);
const $fileAttach = ref();
const base64Imgs = ref<IAttachedImg[]>([]);
const addedFiles = ref<File[]>([]);

const isCommentTextAndPhotos = computed(() => props.commentPostMode === 'TEXT_AND_PHOTOS');

async function handleFileAttach(files: File[]) {
  addedFiles.value = files.filter((file) => file.type.match('image'));

  if (addedFiles.value.length > 3) {
    addedFiles.value = addedFiles.value.slice(-3);
  }

  base64Imgs.value = [
    ...base64Imgs.value,
    ...await Promise.all(addedFiles.value.map(img2Base64))
      .then(
        (res) => res.map(
          (file) => ({
            name: file.img.name,
            size: file.img.size,
            dataSrc: file.base64 as string,
          }),
        ),
      ),
  ];
}

function removeImg(file: IAttachedImg) {
  if (!$fileAttach.value) return;

  clearError();

  base64Imgs.value = base64Imgs.value.filter((attached) => attached.name !== file.name && attached.size !== file.size);
}

const { notify } = useNotification();

async function submitSuccess({ values }: TSubmitSuccess) {
  $addEvent(AnalyticsEvents.ProductCommentAdd, { ...props.product });

  loading.value = true;

  const params: ProductCommentBase64ImagesDataDTO = {
    productId: props.productId,
    imagesBase64: [],
    text: (values.text ?? '').trim(),
  };

  if (props.replyComment?.id) {
    params.parentCommentId = props.replyComment.id;
    params.text = values.text.replace(replyCommentNick.value, '').trim();
  }

  if (base64Imgs.value.length) {
    params.imagesBase64 = base64Imgs.value.map((img) => img.dataSrc);
  }

  const { data, error }: { data: Ref<CommentDTO | null>, error: Ref<CustomHTTPError | null> } = await useAsyncData(() => (isCommentTextAndPhotos.value
    ? postComment(params)
    : postCommentNoPhotos(params)
  ));

  if (data.value) {
    $addEvent(AnalyticsEvents.ProductCommentSuccess, { ...props.product });

    await props.refresh(data.value);

    commentText.value = '';
    addedFiles.value = [];
    base64Imgs.value = [];
  }

  loading.value = false;

  if (error.value) {
    let errorText = 'Пожалуйста, проверьте доступность интернет&#8209;соединения';

    if (error.value.data) {
      const { message, file } = error.value.data;

      if (error.value.statusCode === 400 && message?.includes('запрещенные слова')) {
        errorText = 'В нашей системе запрещено обмениваться контактами с продавцом и использовать ненормативную лексику. За это нарушитель может быть забанен.';
      } else if (message) {
        errorText = message;
      } else if (file) {
        errorText = 'Слишком большой размер загружаемого файла';
      }
    }

    notify({
      group: 'productComments',
      title: 'Ваш комментарий не был отправлен',
      text: errorText,
    });
  }
}

function setFormBlur() {
  if (!isDesktop.value && $input.value) {
    $input.value.setBlur();
  }
}

watch(() => props.replyComment, async (value) => {
  await nextTick();

  if (value) {
    commentText.value = replyCommentNick.value;
  }

  if ($input.value && !props.noFocus) {
    $input.value.setFocus();
  }
}, { immediate: true });

watch(() => commentText.value, (value, oldValue) => {
  if (!replyCommentNick.value) return;

  if ((oldValue.length && value.length < replyCommentNick.value.length)
    || !value.includes(replyCommentNick.value)) {
    emits('update:replyComment');
    commentText.value = '';
  }
});

defineExpose({
  setFormBlur,
});
</script>

<style lang="scss" scoped>
@import "~/assets/scss/settings/index";

.osk-product-comments-form {
  display: flex;
  align-items: flex-end;

  &__images {
    flex-shrink: 0;
    margin-right: $indent-mmedium;
    transform: translateY(-5px);
  }

  &__content {
    width: 100%;
    display: flex;
    align-items: center;
    position: relative;
  }

  &__input {
    width: 100%;
    padding-right: 44px;

    &_round-disable {
      border-radius: 0 0 20px 20px;
    }
  }

  &__button {
    position: absolute;
    bottom: 3px;
    right: 3px;
    margin-top: 0 !important;
  }

  &__wrapper {
    width: 100%;
  }

  :deep(.vue-notification-wrapper) {
    padding: 16px !important;
  }

  &__notification {
    padding: 20px 22px;
    background: #fff;
    box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.12);

    @include media-query(md-and-up) {
      width: 400px;
      margin-right: 36px;
    }
  }

  &__notification-title {
    font-size: 16px;
    font-weight: 700;
    line-height: normal;
    margin-bottom: 12px;
  }

  &__notification-text {
    margin-bottom: 16px;
  }
}
</style>
